/**
 * T&A Admin hub / tab visibility helpers (mirrors backend implication).
 */

const REPORT_LEAF_PREFIX = "timekeeping.reports.";

/** Perms that unlock /people/timesheet/admin/reports (any one is enough). */
export const TA_REPORTS_ROUTE_PERMS = [
  "admin.settings",
  "timekeeping.admin",
  "timekeeping.reports",
  "pto.admin",
  "timekeeping.reports.exceptions",
  "timekeeping.reports.hr",
  "timekeeping.reports.dcaa",
  "timekeeping.reports.audit",
  ...[
    "x01",
    "hr01",
    "hr02",
    "hr03",
    "hr04",
    "hr05",
    "hr06",
    "hr08",
    "dc01",
    "dc02",
    "dc03",
    "dc04",
    "au01",
    "au02",
    "au03",
    "au04",
    "au05",
  ].map((s) => `${REPORT_LEAF_PREFIX}${s}`),
];

export const TA_SETUP_ROUTE_PERMS = [
  "admin.settings",
  "timekeeping.admin",
  "timekeeping.setup.admin",
];

export function hasAnyReportsAccess(hasPermission) {
  if (
    hasPermission("admin.settings") ||
    hasPermission("timekeeping.admin") ||
    hasPermission("timekeeping.reports") ||
    hasPermission("pto.admin")
  ) {
    return true;
  }
  // Segment or leaf (role may store only a child)
  // hasPermission can't list all — check known segments + rely on API filter for leaves.
  return (
    hasPermission("timekeeping.reports.exceptions") ||
    hasPermission("timekeeping.reports.hr") ||
    hasPermission("timekeeping.reports.dcaa") ||
    hasPermission("timekeeping.reports.audit") ||
    // Common least-privilege leaves used in role builder
    [
      "x01",
      "hr01",
      "hr02",
      "hr03",
      "hr04",
      "hr05",
      "hr06",
      "hr08",
      "dc01",
      "dc02",
      "dc03",
      "dc04",
      "au01",
      "au02",
      "au03",
      "au04",
      "au05",
    ].some((s) => hasPermission(`${REPORT_LEAF_PREFIX}${s}`))
  );
}

export function canAccessAttendanceHub(hasPermission) {
  return (
    hasPermission("admin.settings") ||
    hasPermission("timekeeping.attendance.admin") ||
    hasPermission("timekeeping.attendance.download") ||
    hasPermission("timekeeping.admin") ||
    hasPermission("pto.admin")
  );
}

/** Sidebar OR-list for People Admin → Attendance. */
export const TA_ATTENDANCE_SIDEBAR_PERMS = [
  "timekeeping.attendance.admin",
  "timekeeping.attendance.download",
  "timekeeping.admin",
  "pto.admin",
  "admin.settings",
];

export function canAccessLeaveHub(hasPermission) {
  return (
    hasPermission("admin.settings") ||
    hasPermission("pto.admin") ||
    hasPermission("timekeeping.admin")
  );
}

export function canAccessTimeHub(hasPermission) {
  return (
    hasPermission("admin.settings") ||
    hasPermission("timekeeping.admin") ||
    hasPermission("timekeeping.setup.admin") ||
    hasAnyReportsAccess(hasPermission) ||
    hasPermission("timekeeping.clerk_entry.initiate") ||
    hasPermission("timekeeping.reconciliation.manage")
  );
}

/** Sidebar OR-list for People Admin → Time (mirrors canAccessTimeHub). */
export const TA_TIME_SIDEBAR_PERMS = [
  ...TA_REPORTS_ROUTE_PERMS,
  "timekeeping.setup.admin",
  "timekeeping.clerk_entry.initiate",
  "timekeeping.reconciliation.manage",
];

/**
 * First Time-hub path the user can open. Report-only roles must not land on
 * /admin/timesheets (that route requires timekeeping.admin).
 */
export function timeHubSidebarPath(hasPermission) {
  if (hasPermission("admin.settings") || hasPermission("timekeeping.admin")) {
    return "/people/timesheet/admin/timesheets";
  }
  if (hasAnyReportsAccess(hasPermission)) {
    return "/people/timesheet/admin/reports";
  }
  if (hasPermission("timekeeping.setup.admin")) {
    return "/people/timesheet/admin/follow-up-emails";
  }
  if (hasPermission("timekeeping.clerk_entry.initiate")) {
    return "/people/timesheet/clerk-entry";
  }
  if (hasPermission("timekeeping.reconciliation.manage")) {
    return "/people/timesheet/reconciliation";
  }
  return "/people/timesheet/admin/timesheets";
}

export function canAccessSetupHub(hasPermission) {
  return (
    hasPermission("admin.settings") ||
    hasPermission("timekeeping.admin") ||
    hasPermission("timekeeping.setup.admin") ||
    hasPermission("pto.policy.manage")
  );
}

export function hubAccessFns() {
  return {
    attendance: canAccessAttendanceHub,
    leave: canAccessLeaveHub,
    time: canAccessTimeHub,
    setup: canAccessSetupHub,
  };
}

/** Tab-level gate; undefined = inherit hub access. */
export function canAccessTaTab(hasPermission, tab) {
  if (tab.permission) {
    if (Array.isArray(tab.permission)) {
      return tab.permission.some((p) => hasPermission(p));
    }
    return hasPermission(tab.permission);
  }
  if (typeof tab.canAccess === "function") {
    return tab.canAccess(hasPermission);
  }
  return true;
}
